import { Platform } from 'react-native';
import { logger } from './logger';

// Microsoft Clarity Project Configuration
const CLARITY_PROJECT_ID = 'st93x5zbz9'; // Web project ID

// Declare global clarity function for TypeScript
declare global {
  interface Window {
    clarity: {
      (action: 'init', projectId: string): void;
      (action: 'set', key: string, value: string): void;
      (action: 'event', eventName: string): void;
      (action: 'identify', userId: string): void;
    };
  }
}

class ClarityService {
  private isInitialized = false;
  private isEnabled = false;

  constructor() {
    this.isEnabled = this.shouldEnableClarity();
  }

  private shouldEnableClarity(): boolean {
    // Only enable on web platform
    if (Platform.OS !== 'web') {
      return false;
    }

    const isDev = __DEV__;
    const forceEnable = process.env.EXPO_PUBLIC_CLARITY_ENABLED === 'true';
    
    // Check if we're on the production domain
    const isProductionDomain = typeof window !== 'undefined' && 
      (window.location.hostname === 'app.remembr.xyz' || window.location.hostname === 'remembr.xyz');
    
    // Check if we're on localhost for development testing
    const isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    
    console.log('🎯 Clarity: Checking if should enable', { 
      isDev, 
      forceEnable, 
      platform: Platform.OS,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
      isProductionDomain,
      isLocalhost
    });
    
    // Enable Clarity only on:
    // 1. Production domain (app.remembr.xyz or remembr.xyz)
    // 2. Localhost when explicitly enabled via env var
    if (isProductionDomain) {
      return true;
    }
    
    if (isLocalhost && forceEnable) {
      return true;
    }
    
    // Disabled for all other domains/environments
    return false;
  }

  async initialize(): Promise<void> {
    if (!this.isEnabled) {
      console.log('🎯 Clarity: Disabled - not production domain or not explicitly enabled');
      logger.info('Clarity: Disabled - not production domain or not explicitly enabled');
      return;
    }

    if (this.isInitialized) {
      console.log('🎯 Clarity: Already initialized');
      logger.info('Clarity: Already initialized');
      return;
    }

    try {
      if (Platform.OS === 'web') {
        console.log('🎯 Clarity: Initializing web with project ID:', CLARITY_PROJECT_ID);
        
        // Check if Clarity is already loaded
        if (typeof window !== 'undefined' && (window as any).clarity) {
          console.log('🎯 Clarity: Already loaded, initializing...');
          window.clarity('init', CLARITY_PROJECT_ID);
          this.isInitialized = true;
          console.log('🎯 Clarity: Successfully initialized');
          logger.info('Clarity: Successfully initialized');
          return;
        }

        // Inject the standard Microsoft Clarity tracking code
        const script = document.createElement('script');
        script.innerHTML = `
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
        `;
        
        console.log('🎯 Clarity: Injecting tracking script');
        document.head.appendChild(script);
        
        // Wait for the script to load and initialize
        setTimeout(() => {
          if (typeof window !== 'undefined' && (window as any).clarity) {
            console.log('🎯 Clarity: Successfully loaded and ready');
            this.isInitialized = true;
            logger.info('Clarity: Successfully initialized');
          } else {
            console.warn('🎯 Clarity: Script loaded but clarity function not available');
            logger.warn('Clarity: Script loaded but clarity function not available');
          }
        }, 1000);

      } else {
        console.log('🎯 Clarity: Skipping - not web platform');
        logger.info('Clarity: Skipping - not web platform');
      }
    } catch (error) {
      console.error('🎯 Clarity: Failed to initialize', error);
      logger.error('Clarity: Failed to initialize', error);
    }
  }



  /**
   * Set custom user ID for tracking
   */
  setUserId(userId: string): void {
    if (!this.isEnabled) return;

    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined' && (window as any).clarity) {
        console.log('🎯 Clarity: Setting user ID:', userId);
        window.clarity('identify', userId);
        logger.info('Clarity: User ID set', { userId });
      }
    } catch (error) {
      console.error('🎯 Clarity: Failed to set user ID', error);
      logger.error('Clarity: Failed to set user ID', error);
    }
  }

  /**
   * Set custom tag for additional context
   */
  setCustomTag(key: string, value: string): void {
    if (!this.isEnabled) return;

    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined' && (window as any).clarity) {
        console.log('🎯 Clarity: Setting custom tag:', { key, value });
        window.clarity('set', key, value);
        logger.info('Clarity: Custom tag set', { key, value });
      }
    } catch (error) {
      console.error('🎯 Clarity: Failed to set custom tag', error);
      logger.error('Clarity: Failed to set custom tag', error);
    }
  }

  /**
   * Track custom events
   */
  trackEvent(eventName: string, properties?: Record<string, string>): void {
    if (!this.isEnabled) return;

    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined' && (window as any).clarity) {
        console.log('🎯 Clarity: Tracking event:', eventName, properties);
        window.clarity('event', eventName);
        
        if (properties) {
          Object.entries(properties).forEach(([key, value]) => {
            window.clarity('set', key, value);
          });
        }
        
        logger.info('Clarity: Event tracked', { eventName, properties });
      }
    } catch (error) {
      console.error('🎯 Clarity: Failed to track event', error);
      logger.error('Clarity: Failed to track event', error);
    }
  }

  /**
   * Get current session ID (not available in web package)
   */
  async getSessionId(): Promise<string | null> {
    console.log('🎯 Clarity: Session ID not available in web package');
    return null;
  }

  /**
   * Get current session URL (not available in web package)
   */
  async getSessionUrl(): Promise<string | null> {
    console.log('🎯 Clarity: Session URL not available in web package');
    return null;
  }

  /**
   * Check if Clarity is initialized and ready
   */
  isReady(): boolean {
    return this.isEnabled && this.isInitialized;
  }
}

// Export singleton instance
export const clarity = new ClarityService();

// Helper functions for common tracking scenarios
export const clarityHelpers = {
  /**
   * Track user authentication events
   */
  trackAuth: (action: 'login' | 'logout' | 'register' | 'verify_email', method?: string) => {
    clarity.trackEvent('auth', { action, method: method || 'unknown' });
  },

  /**
   * Track navigation events
   */
  trackNavigation: (screenName: string, source?: string) => {
    clarity.trackEvent('navigation', { screen: screenName, source: source || 'unknown' });
  },

  /**
   * Track user interactions
   */
  trackInteraction: (element: string, action: string, value?: string) => {
    clarity.trackEvent('interaction', { element, action, value: value || '' });
  },

  /**
   * Track errors
   */
  trackError: (error: string, context?: string) => {
    clarity.trackEvent('error', { error, context: context || 'unknown' });
  },

  /**
   * Track feature usage
   */
  trackFeature: (feature: string, action: string, metadata?: Record<string, string>) => {
    clarity.trackEvent('feature', { feature, action, ...metadata });
  },
};

// Default export
export default clarity;
